/**
 * Lifted from `lib/insights/resolveInsight.ts` so non-insight callers (e.g. the
 * identity detail page's "Open in graph view" card) can build a `GraphView`
 * from a Person/Relationship payload without importing through the insight
 * resolver.
 */

import type { GraphData, Person, Relationship } from '@/services/api/graph'
import {
  emptyViewState,
  type GraphView,
  type GraphViewEdge,
  type GraphViewNode,
} from '@/types/graph-view'
import { getEntityDisplayLabel } from '@/utils/entity-display'

export function nodeFromPerson(p: Person): GraphViewNode {
  const properties = (p as unknown as { properties?: Record<string, unknown> }).properties ?? {}
  const labels = Array.isArray(p.labels) ? [...p.labels] : []
  const typeLabel = (p as unknown as { typeLabel?: string }).typeLabel ?? labels[0]
  return {
    id: String(p.id),
    // Prefer the schema-stamped ``_display_name`` over the raw ``p.name``
    // so identity-page builders / insight resolver render the same label
    // the rest of the UI shows.
    name: getEntityDisplayLabel({
      id: String(p.id),
      name: p.name ?? undefined,
      labels,
      typeLabel,
      properties,
    }),
    labels,
    typeLabel,
    properties: { ...properties },
  }
}

export function edgeFromRelationship(r: Relationship): GraphViewEdge {
  const properties = (r as unknown as { properties?: Record<string, unknown> }).properties ?? {}
  return {
    id: String(r.id),
    type: String(r.type ?? ''),
    source: String(r.source),
    target: String(r.target),
    properties: { ...properties },
  }
}

export function makeMeta(caseId: string, label: string): GraphView['meta'] {
  return {
    source: 'assistant',
    caseId,
    createdAt: new Date().toISOString(),
    label,
  }
}

/**
 * A node with what the graph links it to, as one view titled `label`: the node first, and once.
 * The neighbours read leaves the node itself out, so it is passed beside them.
 */
export function neighbourhoodView(
  caseId: string,
  label: string,
  centre: Person,
  around: GraphData
): GraphView {
  const others = around.nodes.filter(node => node.id !== centre.id)
  return {
    meta: makeMeta(caseId, label),
    nodes: [centre, ...others].map(nodeFromPerson),
    edges: around.relationships.map(edgeFromRelationship),
    view: emptyViewState(),
  }
}
